// # Basic Calendar Component for Step 1 Mentorship
import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { Checkbox } from '@mui/material';
// # Import Dialog components and ExpandMore icon
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Divider, Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { getFirestore, doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
// # Import role access utility
import m1Access from '../m1_access';
// # Import library navigation button icon
import { FaBookMedical } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
// # Import cache manager
import cacheManager from '../../../auth/cache_manager';
import { db } from '../../../firebase/config';
import { updateDoc } from 'firebase/firestore';

// # Import FullCalendar styles
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import '@fullcalendar/list/main.css';
import './m1_calendar.css';

function M1Calendar({ isAdmin, isTutor, isMentorView, menteeId }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { username } = useParams();
  const [tasks, setTasks] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [taskDetails, setTaskDetails] = useState(null);
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Create base path based on user role
  const basePath = m1Access.getBasePath({
    isAdmin,
    isTutor,
    isMentorView,
    username
  });

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Listen for theme changes from DashboardHeader
  useEffect(() => {
    const handleThemeChange = (event) => {
      setIsDarkMode(event.detail.isDark);
    };
    
    window.addEventListener('theme_change', handleThemeChange);
    
    return () => {
      window.removeEventListener('theme_change', handleThemeChange);
    };
  }, []);

  // # Get the correct user ID based on role context
  useEffect(() => {
    // # Get menteeId from props or sessionStorage
    const storedMenteeId = menteeId || sessionStorage.getItem('currentViewingMenteeId');
    
    // # Use the access utility to determine the target user ID
    const userId = m1Access.getTargetUserId({
      isAdmin,
      isTutor,
      viewingMenteeId: storedMenteeId,
      currentUserId: user?.uid
    });
    
    setTargetUserId(userId);
    setIsMentor(userId !== user?.uid);
  }, [user?.uid, isAdmin, isTutor, menteeId]);

  // # Load tasks from Firestore
  useEffect(() => {
    const loadTasks = async () => {
      if (!targetUserId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const db = getFirestore();
        const menteeRef = doc(db, 'db1_mentee', targetUserId);
        const menteeSnapshot = await getDoc(menteeRef);
        
        if (menteeSnapshot.exists()) {
          const menteeData = menteeSnapshot.data();
          const allTasks = [];
          
          // # Process all chapters and their tasks
          Object.entries(menteeData.chapters || {}).forEach(([chapterId, chapterData]) => {
            // # Get chapter metadata
            const chapterName = chapterData.metadata?.chapterName || chapterId;
            
            // # Process tasks in this chapter
            Object.entries(chapterData.tasks || {}).forEach(([taskId, task]) => {
              allTasks.push({
                id: taskId,
                ...task,
                chapter: chapterId,
                chapterName: chapterName
              });
            });
          });
          
          setTasks(allTasks);
        } else {
          setTasks([]);
        }
      } catch (error) {
        console.error('Error loading tasks:', error);
        setError('Failed to load tasks. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadTasks();
  }, [targetUserId]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Navigate to library function
  const navigateToLibrary = () => {
    // # Navigate to appropriate library based on role
    if (isAdmin) {
      navigate('/admin/library/step1');
    } else if (isTutor) {
      navigate('/tutor/library/step1');
    } else {
      navigate('/library/step1');
    }
  };

  // # Handle task completion toggle
  const handleToggleComplete = async (e, taskId, chapterId) => {
    // # Prevent event propagation and default behavior
    e.preventDefault();
    e.stopPropagation();
    
    if (!targetUserId) return;
    
    try {
      // # Store current scroll position
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      
      // # Get the event element for smooth transition
      const eventElement = e.target.closest('.mnt_cld1_event');
      if (eventElement) {
        eventElement.style.pointerEvents = 'none'; // # Prevent multiple clicks
        eventElement.classList.add('updating'); // # Add transition class
      }

      const db = getFirestore();
      const menteeRef = doc(db, 'db1_mentee', targetUserId);
      const menteeSnapshot = await getDoc(menteeRef);
      
      if (menteeSnapshot.exists()) {
        const menteeData = menteeSnapshot.data();
        const chapters = menteeData.chapters || {};
        const chapter = chapters[chapterId] || {};
        const tasks = chapter.tasks || {};
        
        // # Update the task's completion status
        const updatedTask = {
          ...tasks[taskId],
          done: !tasks[taskId].done
        };
        
        // # Update local state first for immediate feedback
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId 
              ? { ...task, done: !task.done }
              : task
          )
        );

        // # Update Firestore in the background
        await setDoc(menteeRef, {
          chapters: {
            ...chapters,
            [chapterId]: {
              ...chapter,
              tasks: {
                ...tasks,
                [taskId]: updatedTask
              }
            }
          }
        }, { merge: true });
        
        // # Restore scroll position after state update
        window.scrollTo({
          top: scrollPosition,
          behavior: 'auto'
        });
      }
    } catch (error) {
      console.error('Error updating task:', error);
    } finally {
      // # Re-enable interactions after update
      const eventElement = e.target.closest('.mnt_cld1_event');
      if (eventElement) {
        setTimeout(() => {
          eventElement.style.pointerEvents = 'auto';
          eventElement.classList.remove('updating');
        }, 300);
      }
    }
  };

  // # Fetch detailed task data from Firestore
  const fetchTaskDetails = async (taskId, chapterId) => {
    if (!targetUserId || !taskId || !chapterId) return null;
    
    try {
      const db = getFirestore();
      const menteeRef = doc(db, 'db1_mentee', targetUserId);
      const menteeSnapshot = await getDoc(menteeRef);
      
      if (menteeSnapshot.exists()) {
        const menteeData = menteeSnapshot.data();
        const chapters = menteeData.chapters || {};
        const chapter = chapters[chapterId] || {};
        const tasks = chapter.tasks || {};
        
        // # Get the specific task
        const taskData = tasks[taskId];
        
        if (taskData) {
          // # Return task data with chapter info
          return {
            ...taskData,
            chapter: chapterId
          };
        }
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
    }
    
    return null;
  };

  // # Handle expand button click to show task details
  const handleExpandClick = async (e, task) => {
    // # Prevent event propagation and default behavior
    e.preventDefault();
    e.stopPropagation();
    
    // # Set the selected task
    setSelectedTask(task);
    
    // # Fetch full task details if needed
    const fullDetails = await fetchTaskDetails(task.id, task.chapter);
    setTaskDetails(fullDetails || task);
    
    // # Open the dialog
    setOpenDialog(true);
  };

  // # Close the task detail dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTask(null);
    setTaskDetails(null);
  };

  // # Get filtered tasks based on active filter
  const getFilteredTasks = () => {
    switch (activeFilter) {
      case 'completed':
        return tasks.filter(task => task.done);
      case 'pending':
        return tasks.filter(task => !task.done);
      default:
        return tasks;
    }
  };

  // # Get counts for each filter
  const getCounts = () => ({
    all: tasks.length,
    completed: tasks.filter(task => task.done).length,
    pending: tasks.filter(task => !task.done).length
  });

  const counts = getCounts();
  const filteredTasks = getFilteredTasks();

  // # Format date for display in the dialog
  const formatDate = (dateString) => {
    if (!dateString) return 'No date specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // # Render task detail fields in two columns
  const renderTaskDetailFields = (task) => {
    if (!task) return null;
    
    // # Fields to exclude
    const excludeFields = ['id', 'status', 'plan']; // # Exclude id, status, and plan fields
    
    // # Fields to format specially
    const dateFields = ['date', 'created_at', 'updated_at', 'due_date', 'start_date', 'end_date'];
    const statusFields = ['done', 'completed', 'is_complete'];
    
    // # Define field order and grouping for better organization
    const leftColumnFields = [
      'task_name', 'chapter', 'subchapter', 'source', 'type', 'step'
    ];
    
    const rightColumnFields = [
      'date', 'day', 'duration'
      // 'done' is handled separately
    ];
    
    // # Get all keys from the task object
    const taskKeys = Object.keys(task).filter(key => !excludeFields.includes(key) && !statusFields.includes(key));
    
    // # Separate fields into left and right columns
    const leftFields = taskKeys.filter(key => leftColumnFields.includes(key));
    const rightFields = taskKeys.filter(key => rightColumnFields.includes(key));
    
    // # Add remaining fields to appropriate columns to balance them
    const remainingFields = taskKeys.filter(key => 
      !leftColumnFields.includes(key) && 
      !rightColumnFields.includes(key) &&
      !excludeFields.includes(key) &&
      !statusFields.includes(key)
    );
    
    // # Distribute remaining fields evenly
    remainingFields.forEach((field, index) => {
      if (index % 2 === 0) {
        leftFields.push(field);
      } else {
        rightFields.push(field);
      }
    });
    
    // # Helper function to render a single field
    const renderField = (key) => {
      // # Skip rendering if value is undefined or null
      if (task[key] === undefined || task[key] === null) return null;
      
      // # Format field label
      const fieldLabel = key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
      
      // # Format field value based on type
      let fieldValue = task[key];
      let fieldClass = '';
      
      // # Handle date fields
      if (dateFields.includes(key) && fieldValue) {
        fieldValue = formatDate(fieldValue);
      }
      
      // # Handle duration field - display as minutes
      if (key === 'duration') {
        fieldValue = `${fieldValue} min`;
      }
      
      // # Handle boolean fields
      if (typeof fieldValue === 'boolean' && !statusFields.includes(key)) {
        fieldValue = fieldValue ? 'Yes' : 'No';
      }
      
      // # Handle object fields
      if (typeof fieldValue === 'object' && fieldValue !== null) {
        return null; // Skip rendering complex objects
      }
      
      return (
        <div key={key} className="mnt_cld1_dialog_field_box">
          <div className="mnt_cld1_dialog_field_row">
            <Typography variant="subtitle2" className="mnt_cld1_dialog_label">
              {fieldLabel}:
            </Typography>
            <Typography 
              variant="body1" 
              className={`mnt_cld1_dialog_value ${fieldClass}`}
            >
              {fieldValue}
            </Typography>
          </div>
        </div>
      );
    };
    
    // # Render description and notes separately (full width)
    const descriptionFields = ['description', 'notes'].filter(key => 
      task[key] !== undefined && task[key] !== null
    );
    
    // # Determine completion status
    const isCompleted = task.done === true || task.completed === true || task.status === 'completed';
    
    return (
      <div className="mnt_cld1_dialog_task_details">
        {/* Completion Status Icon */}
        <div className="mnt_cld1_dialog_status_container">
          <div className={`mnt_cld1_dialog_status_icon ${isCompleted ? 'completed' : 'pending'}`}>
            {isCompleted ? (
              <CheckCircleIcon className="mnt_cld1_status_icon" />
            ) : (
              <RadioButtonUncheckedIcon className="mnt_cld1_status_icon" />
            )}
          </div>
        </div>
        
        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} sm={6}>
            <div className="mnt_cld1_dialog_column">
              {leftFields.map(renderField)}
            </div>
          </Grid>
          
          {/* Right Column */}
          <Grid item xs={12} sm={6}>
            <div className="mnt_cld1_dialog_column">
              {rightFields.map(renderField)}
            </div>
          </Grid>
          
          {/* Full Width Description Fields */}
          {descriptionFields.length > 0 && (
            <Grid item xs={12}>
              <Divider className="mnt_cld1_dialog_divider" />
              {descriptionFields.map(key => {
                const fieldLabel = key
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, l => l.toUpperCase());
                
                return (
                  <div 
                    key={key} 
                    className="mnt_cld1_dialog_field_box mnt_cld1_dialog_description"
                  >
                    <div className="mnt_cld1_dialog_field_row">
                      <Typography variant="subtitle2" className="mnt_cld1_dialog_label">
                        {fieldLabel}:
                      </Typography>
                      <Typography variant="body1" className="mnt_cld1_dialog_value">
                        {task[key]}
                      </Typography>
                    </div>
                  </div>
                );
              })}
            </Grid>
          )}
        </Grid>
      </div>
    );
  };

  // # Handle event drop (date change)
  const handleEventDrop = async (info) => {
    // # Only allow mentors/tutors to update dates
    if (!isAdmin && !isTutor) {
      info.revert();
      return;
    }

    try {
      const { event } = info;
      const taskId = event.extendedProps.taskId;
      const chapterId = event.extendedProps.chapter;
      const newDate = event.startStr;

      if (!targetUserId || !taskId || !chapterId) {
        info.revert();
        return;
      }

      const db = getFirestore();
      const menteeRef = doc(db, 'db1_mentee', targetUserId);
      const menteeSnapshot = await getDoc(menteeRef);

      if (menteeSnapshot.exists()) {
        const menteeData = menteeSnapshot.data();
        const chapters = menteeData.chapters || {};
        const chapter = chapters[chapterId] || {};
        const tasks = chapter.tasks || {};
        const task = tasks[taskId];

        if (task) {
          // # Update the task date
          const updatedTask = {
            ...task,
            date: newDate
          };

          // # Update Firestore
          await setDoc(menteeRef, {
            chapters: {
              ...chapters,
              [chapterId]: {
                ...chapter,
                tasks: {
                  ...tasks,
                  [taskId]: updatedTask
                }
              }
            }
          }, { merge: true });

          // # Update local state
          setTasks(prevTasks => 
            prevTasks.map(t => 
              t.id === taskId 
                ? { ...t, date: newDate }
                : t
            )
          );
        }
      }
    } catch (error) {
      console.error('Error updating task date:', error);
      info.revert();
    }
  };

  return (
    <div className={`mnt_cld1_container ${isDarkMode ? 'mnt_cld1_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      {/* # Library Navigation Button */}
      <div className="mnt_cld1_library_nav_button" onClick={navigateToLibrary} title="Go to Step 1 Library">
        <FaBookMedical />
        <span>Library</span>
      </div>
      
      <div className="mnt_cld1_main">
        <div className="mnt_cld1_title_section">
          <h1 className="mnt_cld1_title">
            Step 1 Study Calendar
            {isMentor && <span className="mnt_cld1_mentor_pill">
              {isAdmin ? 'Mentor' : 'Tutor'}
            </span>}
          </h1>
          
          {/* Add mentee info when viewing as mentor/tutor */}
          {isMentor && username && (
            <div className="mnt_cld1_mentee_info">
              Viewing calendar for: @{username}
            </div>
          )}
          
          <div className="mnt_cld1_filters">
            <button 
              className={`mnt_cld1_filter_btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All Tasks
              <span className="mnt_cld1_filter_count">{tasks.length}</span>
            </button>
            <button 
              className={`mnt_cld1_filter_btn ${activeFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveFilter('completed')}
            >
              Complete
              <span className="mnt_cld1_filter_count">
                {tasks.filter(task => task.done).length}
              </span>
            </button>
            <button 
              className={`mnt_cld1_filter_btn ${activeFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveFilter('pending')}
            >
              Pending
              <span className="mnt_cld1_filter_count">
                {tasks.filter(task => !task.done).length}
              </span>
            </button>
          </div>
        </div>
        
        <div className="mnt_cld1_calendar_section">
          <div className="mnt_cld1_calendar_content">
            {loading ? (
              <div className="mnt_cld1_loading">
                <div className="mnt_cld1_loading_spinner"></div>
                <p>Loading calendar...</p>
              </div>
            ) : error ? (
              <div className="mnt_cld1_error">
                <p>{error}</p>
              </div>
            ) : (
              <div className="mnt_cld1_wrapper">
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
                  }}
                  dayHeaderFormat={{ weekday: 'short' }}
                  dayHeaderContent={(args) => {
                    return (
                      <span className="fc-col-header-cell-cushion">
                        {args.text.toUpperCase()}
                      </span>
                    );
                  }}
                  events={tasks
                    .filter(task => {
                      if (activeFilter === 'completed') return task.done;
                      if (activeFilter === 'pending') return !task.done;
                      return true;
                    })
                    .map(task => ({
                      id: task.id,
                      title: task.task_name,
                      start: task.date,
                      allDay: true,
                      extendedProps: {
                        chapter: task.chapter,
                        chapterName: task.chapterName,
                        source: task.source,
                        done: task.done,
                        taskId: task.id,
                        task: task
                      }
                    }))
                  }
                  eventContent={(eventInfo) => {
                    const sourceClass = eventInfo.event.extendedProps.source ? 
                      `source-${eventInfo.event.extendedProps.source.toLowerCase().replace(/\s+/g, '')}` : '';
                    
                    return (
                      <div className={`mnt_cld1_event ${sourceClass} ${eventInfo.event.extendedProps.done ? 'completed' : ''}`}>
                        <div className="mnt_cld1_event_header">
                          <h1 className="mnt_cld1_event_title">{eventInfo.event.title}</h1>
                          <h2 className="mnt_cld1_event_chapter">{eventInfo.event.extendedProps.chapterName}</h2>
                        </div>
                        <div className="mnt_cld1_event_body">
                          <h3 className="mnt_cld1_event_source">{eventInfo.event.extendedProps.source}</h3>
                          <div className="mnt_cld1_event_actions">
                            <Checkbox
                              checked={eventInfo.event.extendedProps.done || false}
                              onChange={(e) => handleToggleComplete(
                                e,
                                eventInfo.event.extendedProps.taskId,
                                eventInfo.event.extendedProps.chapter
                              )}
                              className="mnt_cld1_event_checkbox"
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <IconButton
                              className="mnt_cld1_event_expand"
                              size="small"
                              onClick={(e) => handleExpandClick(e, eventInfo.event.extendedProps.task)}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                  editable={isAdmin || isTutor}
                  droppable={isAdmin || isTutor}
                  eventDrop={handleEventDrop}
                  eventStartEditable={isAdmin || isTutor}
                  eventDurationEditable={false}
                  selectable={true}
                  selectMirror={true}
                  dayMaxEvents={false}
                  weekends={true}
                  height="auto"
                  contentHeight="auto"
                  aspectRatio={1}
                  stickyHeaderDates={true}
                  dayCellDidMount={(info) => {
                    info.el.style.minHeight = '120px';
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />

      {/* # Task Detail Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        className="mnt_cld1_dialog"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="mnt_cld1_dialog_title">
          {taskDetails?.task_name || selectedTask?.task_name || 'Task Details'}
        </DialogTitle>
        <DialogContent className="mnt_cld1_dialog_content">
          {renderTaskDetailFields(taskDetails || selectedTask)}
        </DialogContent>
        <DialogActions className="mnt_cld1_dialog_actions">
          <Button onClick={handleCloseDialog} color="primary" className="mnt_cld1_dialog_button">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default M1Calendar;
